import React, { Component } from "react";
import PropTypes from "prop-types";

import { head, last, noop, hexToRGBA } from "../../utils";
import { getXValue } from "../../utils/ChartDataUtil";
import { saveNodeType, isHover } from "../utils";

import { getNewXY } from "./EachTrendLine";
import StraightLine, { generateLine } from "../components/StraightLine";
import ClickableCircle from "../components/ClickableCircle";
import HoverTextNearMouse from "../components/HoverTextNearMouse";
import Text from "../components/Text";
import GenericChartComponent from "../../GenericChartComponent";
import { getMouseCanvas } from "../../GenericComponent";

class EachFibProjection extends Component {
  constructor(props) {
    super(props);

    this.handlePoint1Drag = this.handlePoint1Drag.bind(this);
    this.handlePoint2Drag = this.handlePoint2Drag.bind(this);
    this.handlePoint3Drag = this.handlePoint3Drag.bind(this);

    this.handleLineMove = this.handleLineMove.bind(this);
    this.handleLineDragStart = this.handleLineDragStart.bind(this);

    this.handleHover = this.handleHover.bind(this);

    this.isHover = isHover.bind(this);
    this.saveNodeType = saveNodeType.bind(this);
    this.nodes = {};

    this.state = {
      hover: false,
    };
  }

  handleHover(moreProps) {
    if (this.state.hover !== moreProps.hovering) {
      this.setState({
        hover: moreProps.hovering,
      });
    }
  }

  handleLineDragStart() {
    const { x1, y1, x2, y2, x3, y3 } = this.props;

    this.dragStart = {
      x1,
      y1,
      x2,
      y2,
      x3,
      y3,
    };
  }

  handleLineMove(moreProps) {
    const { index, onDrag } = this.props;

    const {
      x1: x1Value,
      y1: y1Value,
      x2: x2Value,
      y2: y2Value,
      x3: x3Value,
      y3: y3Value,
    } = this.dragStart;

    const {
      xScale,
      chartConfig: { yScale },
      xAccessor,
      fullData,
    } = moreProps;
    const { startPos, mouseXY } = moreProps;

    const x1 = xScale(x1Value);
    const y1 = yScale(y1Value);
    const x2 = xScale(x2Value);
    const y2 = yScale(y2Value);
    const x3 = xScale(x3Value);
    const y3 = yScale(y3Value);

    const dx = startPos[0] - mouseXY[0];
    const dy = startPos[1] - mouseXY[1];

    const newX1Value = getXValue(
      xScale,
      xAccessor,
      [x1 - dx, y1 - dy],
      fullData,
    );
    const newY1Value = yScale.invert(y1 - dy);
    const newX2Value = getXValue(
      xScale,
      xAccessor,
      [x2 - dx, y2 - dy],
      fullData,
    );
    const newY2Value = yScale.invert(y2 - dy);
    const newX3Value = getXValue(
      xScale,
      xAccessor,
      [x3 - dx, y3 - dy],
      fullData,
    );
    const newY3Value = yScale.invert(y3 - dy);

    onDrag(index, {
      x1: newX1Value,
      y1: newY1Value,
      x2: newX2Value,
      y2: newY2Value,
      x3: newX3Value,
      y3: newY3Value,
    });
  }

  handlePoint1Drag(moreProps) {
    const [x1, y1] = getNewXY(moreProps);
    const { onPoint1Drag } = this.props;
    onPoint1Drag([x1, y1]);
  }

  handlePoint2Drag(moreProps) {
    const [x2, y2] = getNewXY(moreProps);
    const { onPoint2Drag } = this.props;
    onPoint2Drag([x2, y2]);
  }

  handlePoint3Drag(moreProps) {
    const [x3, y3] = getNewXY(moreProps);
    const { onPoint3Drag } = this.props;
    onPoint3Drag([x3, y3]);
  }

  render() {
    const { x1, x2, x3, y1, y2, y3 } = this.props;
    const { interactive, appearance } = this.props;
    const yDisplayFormat = this.props.yDisplayFormat || ((v) => v.toFixed(2));

    // Don't render if missing first two points (at minimum)
    if (x1 === null || x2 === null) return null;

    // x3 can be null during drawing (after first click, before third click)
    const hasThirdPoint = x3 !== null && y3 !== null;

    // Extract values from registry format consistently
    const stroke = appearance.stroke?.color || "#FF9800";
    const strokeWidth = appearance.stroke?.width || 2;
    const strokeOpacity =
      appearance.stroke?.opacity !== undefined ? appearance.stroke.opacity : 1;
    const strokeDasharray = appearance.stroke?.dash || "Solid";

    const fontFamily =
      appearance.font?.fontFamily ||
      "Helvetica Neue, Helvetica, Arial, sans-serif";
    const fontSize = appearance.font?.fontSize || 11;
    const fontFill = appearance.font?.color || "#FF9800";

    const fillColor = appearance.fill?.color || "#FF9800";
    const fillOpacity =
      appearance.fill?.opacity !== undefined ? appearance.fill.opacity : 0.1;

    const { r } = appearance;

    // Get theme-aware edge colors for maximum visibility
    const edgeColors = {
      stroke: "#000000", // Black stroke
      fill: "#FFFFFF", // White fill
    };

    const actualEdgeFill = edgeColors.fill;
    // Force larger radius for better visibility
    const circleRadius = r || 6;

    const { hoverText, selected } = this.props;
    const { hover } = this.state;
    const { onDragComplete } = this.props;

    // Only calculate projection lines if we have all 3 points
    const configuredLevels = appearance.levels || DEFAULT_PROJECTION_LEVELS;
    const activeLevels = configuredLevels.filter((l) => l.display !== false);
    const lines = hasThirdPoint
      ? helper({ x1, y1, x2, y2, x3, y3, levels: activeLevels })
      : [];

    const {
      enable: hoverTextEnabled,
      selectedText: hoverTextSelected,
      text: hoverTextUnselected,
      ...restHoverTextProps
    } = hoverText;

    const dir =
      lines.length > 0 ? (head(lines).y > last(lines).y ? 3 : -1.3) : 3;

    return (
      <g>
        {/* Filled areas between Fibonacci levels - starts from x3 to right edge */}
        {/* Gradient opacity: more opaque near y3, more transparent away from y3 */}
        {lines.length > 1 &&
          lines.map((line, j) => {
            if (j === lines.length - 1) return null; // Skip last item (no level below it)

            const nextLine = lines[j + 1];
            const y1 = line.y;
            const y2 = nextLine.y;

            // Calculate gradient opacity based on distance from y3
            // First fill (j=0, closest to y3) is most opaque
            // Each successive fill gets more transparent
            const gradientFactor = 1 - j * 0.25; // Decreases by 25% each level
            const adjustedOpacity = fillOpacity * Math.max(gradientFactor, 0.1); // Minimum 10% of base opacity

            return (
              <FibFillArea
                key={`fill_${j}`}
                x1={x3}
                y1={y1}
                y2={y2}
                fill={fillColor}
                fillOpacity={adjustedOpacity}
              />
            );
          })}

        {/* Trend line 1: p1 → p2 (measurement line) */}
        <StraightLine
          ref={this.saveNodeType("trendline1")}
          selected={selected || hover}
          type="LINE"
          x1Value={x1}
          y1Value={y1}
          x2Value={x2}
          y2Value={y2}
          stroke={stroke}
          strokeWidth={hover || selected ? strokeWidth + 1 : strokeWidth}
          strokeOpacity={strokeOpacity * 0.7}
          strokeDasharray="Solid"
          interactiveCursorClass="react-stockcharts-move-cursor"
          onHover={interactive ? this.handleHover : undefined}
          onUnHover={interactive ? this.handleHover : undefined}
          onDragStart={this.handleLineDragStart}
          onDrag={this.handleLineMove}
          onDragComplete={onDragComplete}
        />

        {/* Trend line 2: p2 → p3 (projection base line) - only show if 3rd point exists */}
        {hasThirdPoint && (
          <StraightLine
            ref={this.saveNodeType("trendline2")}
            selected={selected || hover}
            type="LINE"
            x1Value={x2}
            y1Value={y2}
            x2Value={x3}
            y2Value={y3}
            stroke={stroke}
            strokeWidth={hover || selected ? strokeWidth + 1 : strokeWidth}
            strokeOpacity={strokeOpacity * 0.7}
            strokeDasharray="Solid"
            interactiveCursorClass="react-stockcharts-move-cursor"
            onHover={interactive ? this.handleHover : undefined}
            onUnHover={interactive ? this.handleHover : undefined}
            onDragStart={this.handleLineDragStart}
            onDrag={this.handleLineMove}
            onDragComplete={onDragComplete}
          />
        )}

        {/* Fibonacci level lines */}
        {lines.map((line, j) => {
          const text = `${yDisplayFormat(line.y)} (${line.percent.toFixed(1)}%)`;

          const xyProvider = ({ xScale, chartConfig }) => {
            const { yScale } = chartConfig;
            // Position label at x3 (start of horizontal lines)
            const x = xScale(x3) + 10;
            const y = yScale(line.y) + dir * 4;
            return [x, y];
          };

          const hoverHandler = interactive
            ? { onHover: this.handleHover, onUnHover: this.handleHover }
            : {};

          return (
            <g key={j}>
              {/* Level line - RAY extends from x3 to the right */}
              <StraightLine
                ref={this.saveNodeType(`line_${j}`)}
                selected={selected || hover}
                {...hoverHandler}
                type="RAY"
                x1Value={line.x1}
                y1Value={line.y}
                x2Value={line.x2}
                y2Value={line.y}
                stroke={stroke}
                strokeWidth={hover || selected ? strokeWidth + 1 : strokeWidth}
                strokeOpacity={strokeOpacity * 0.8}
                strokeDasharray={strokeDasharray}
                interactiveCursorClass="react-stockcharts-move-cursor"
                onDragStart={this.handleLineDragStart}
                onDrag={this.handleLineMove}
                onDragComplete={onDragComplete}
              />

              {/* Percentage label */}
              <Text
                selected={selected}
                xyProvider={xyProvider}
                fontFamily={fontFamily}
                fontSize={fontSize}
                fill={fontFill}
              >
                {text}
              </Text>
            </g>
          );
        })}

        {/* Drag handles for points */}
        <ClickableCircle
          ref={this.saveNodeType("point1")}
          show={selected || hover}
          cx={x1}
          cy={y1}
          r={circleRadius}
          fill={actualEdgeFill}
          stroke={edgeColors.stroke}
          strokeWidth={2}
          strokeOpacity={1}
          interactiveCursorClass="react-stockcharts-move-cursor"
          onDrag={this.handlePoint1Drag}
          onDragComplete={onDragComplete}
        />
        <ClickableCircle
          ref={this.saveNodeType("point2")}
          show={selected || hover}
          cx={x2}
          cy={y2}
          r={circleRadius}
          fill={actualEdgeFill}
          stroke={edgeColors.stroke}
          strokeWidth={2}
          strokeOpacity={1}
          interactiveCursorClass="react-stockcharts-move-cursor"
          onDrag={this.handlePoint2Drag}
          onDragComplete={onDragComplete}
        />
        {/* Third drag handle - only show if 3rd point exists */}
        {hasThirdPoint && (
          <ClickableCircle
            ref={this.saveNodeType("point3")}
            show={selected || hover}
            cx={x3}
            cy={y3}
            r={circleRadius}
            fill={actualEdgeFill}
            stroke={edgeColors.stroke}
            strokeWidth={2}
            strokeOpacity={1}
            interactiveCursorClass="react-stockcharts-move-cursor"
            onDrag={this.handlePoint3Drag}
            onDragComplete={onDragComplete}
          />
        )}

        <HoverTextNearMouse
          show={hoverTextEnabled && hover}
          {...restHoverTextProps}
          text={selected ? hoverTextSelected : hoverTextUnselected}
        />
      </g>
    );
  }
}

// Component to render filled area between two Fibonacci levels
class FibFillArea extends Component {
  constructor(props) {
    super(props);
    this.renderSVG = this.renderSVG.bind(this);
    this.drawOnCanvas = this.drawOnCanvas.bind(this);
  }

  drawOnCanvas(ctx, moreProps) {
    const { x1, y1, y2, fill, fillOpacity } = this.props;
    const {
      xScale,
      chartConfig: { yScale },
    } = moreProps;

    // Get chart's right edge
    const [, xRight] = xScale.range();

    // Start from x1 (first point) and extend to right edge
    const x1Px = xScale(x1);
    const y1Px = yScale(y1);
    const y2Px = yScale(y2);

    ctx.fillStyle = hexToRGBA(fill, fillOpacity);
    ctx.beginPath();
    ctx.rect(x1Px, Math.min(y1Px, y2Px), xRight - x1Px, Math.abs(y2Px - y1Px));
    ctx.fill();
  }

  renderSVG(moreProps) {
    const { x1, y1, y2, fill, fillOpacity } = this.props;
    const {
      xScale,
      chartConfig: { yScale },
    } = moreProps;

    // Get chart's right edge
    const [, xRight] = xScale.range();

    // Start from x1 (first point) and extend to right edge
    const x1Px = xScale(x1);
    const y1Px = yScale(y1);
    const y2Px = yScale(y2);

    const y = Math.min(y1Px, y2Px);
    const width = xRight - x1Px;
    const height = Math.abs(y2Px - y1Px);

    return (
      <rect
        x={x1Px}
        y={y}
        width={width}
        height={height}
        fill={fill}
        fillOpacity={fillOpacity}
      />
    );
  }

  render() {
    return (
      <GenericChartComponent
        svgDraw={this.renderSVG}
        canvasToDraw={getMouseCanvas}
        canvasDraw={this.drawOnCanvas}
        drawOn={["mousemove", "mouseleave", "pan", "drag", "xAxisZoom"]}
      />
    );
  }
}

FibFillArea.propTypes = {
  x1: PropTypes.any.isRequired,
  y1: PropTypes.number.isRequired,
  y2: PropTypes.number.isRequired,
  fill: PropTypes.string.isRequired,
  fillOpacity: PropTypes.number.isRequired,
};

const DEFAULT_PROJECTION_LEVELS = [
  { level: 0, display: true },
  { level: 61.8, display: true },
  { level: 100, display: true },
  { level: 127.2, display: true },
  { level: 161.8, display: true },
  { level: 261.8, display: true },
  { level: 423.6, display: true },
];

// Helper function to calculate Fibonacci projection levels
function helper({ x1, y1, x2, y2, x3, y3, levels }) {
  // Measure distance between first two points
  const distance = y2 - y1;

  // Project levels from third point
  // Lines start at x3 and extend to the right
  // Use x3 + positive offset to ensure RAY extends rightward
  const projections = levels.map((each) => ({
    percent: each.level,
    x1: x3, // Start from third point
    x2: x3 + Math.abs(x2 - x1), // Ensure x2 > x3 for rightward direction
    y: y3 + (distance * each.level) / 100,
  }));

  return projections;
}

EachFibProjection.propTypes = {
  x1: PropTypes.any.isRequired,
  x2: PropTypes.any.isRequired,
  x3: PropTypes.any.isRequired,
  y1: PropTypes.number.isRequired,
  y2: PropTypes.number.isRequired,
  y3: PropTypes.number.isRequired,

  yDisplayFormat: PropTypes.func,
  selected: PropTypes.bool.isRequired,

  appearance: PropTypes.shape({
    // Registry format (primary)
    stroke: PropTypes.oneOfType([
      PropTypes.shape({
        color: PropTypes.string,
        dash: PropTypes.string,
        width: PropTypes.number,
        opacity: PropTypes.number,
      }),
      PropTypes.string, // Legacy support
    ]),
    font: PropTypes.shape({
      fontFamily: PropTypes.string,
      fontSize: PropTypes.number,
      color: PropTypes.string,
    }),
    fill: PropTypes.shape({
      color: PropTypes.string,
      opacity: PropTypes.number,
    }),
    // Other properties
    edgeFill: PropTypes.string.isRequired,
    edgeStroke: PropTypes.shape({
      color: PropTypes.string,
      opacity: PropTypes.number,
    }),
    r: PropTypes.number,
  }).isRequired,

  interactive: PropTypes.bool.isRequired,
  index: PropTypes.number,
  hoverText: PropTypes.object.isRequired,

  onDrag: PropTypes.func.isRequired,
  onDragComplete: PropTypes.func.isRequired,
  onPoint1Drag: PropTypes.func,
  onPoint2Drag: PropTypes.func,
  onPoint3Drag: PropTypes.func,
};

EachFibProjection.defaultProps = {
  interactive: true,
  yDisplayFormat: (v) => v.toFixed(2),
  onDrag: noop,
  onDragComplete: noop,
  onPoint1Drag: noop,
  onPoint2Drag: noop,
  onPoint3Drag: noop,
};

export default EachFibProjection;
